import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { isNight } from '@/lib/sun';

type Theme = 'light' | 'dark' | 'system' | 'sun';

interface Ctx {
  theme: Theme;
  resolved: 'light' | 'dark';
  setTheme: (t: Theme) => void;
}

const ThemeContext = createContext<Ctx | null>(null);
const KEY = 'flax-theme';

function getSystem(): 'light' | 'dark' {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

function getSunResolved(): 'light' | 'dark' {
  return isNight() ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'sun';
    return (localStorage.getItem(KEY) as Theme | null) ?? 'sun';
  });
  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>(getSystem);
  const [sunResolved, setSunResolved] = useState<'light' | 'dark'>(getSunResolved);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemTheme(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Re-evaluate sun-based resolution every 5 minutes so the dashboard flips
  // around sunset without a reload.
  useEffect(() => {
    if (theme !== 'sun') return;
    const tick = () => setSunResolved(getSunResolved());
    tick();
    const id = window.setInterval(tick, 5 * 60 * 1000);
    return () => window.clearInterval(id);
  }, [theme]);

  const resolved: 'light' | 'dark' =
    theme === 'system' ? systemTheme : theme === 'sun' ? sunResolved : theme;

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(resolved);
    root.style.colorScheme = resolved;
  }, [resolved]);

  const setTheme = (t: Theme) => {
    setThemeState(t);
    localStorage.setItem(KEY, t);
  };

  return (
    <ThemeContext.Provider value={{ theme, resolved, setTheme }}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): Ctx {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
